import {Component, Inject, LOCALE_ID, OnDestroy, OnInit} from '@angular/core';
import {MatSnackBar, MatSnackBarConfig, MatSnackBarHorizontalPosition, MatSnackBarVerticalPosition} from '@angular/material/snack-bar';
import {State} from '@carm/_configs/store/reducers';
import {MetadataService} from '@carm/_core/metadata/services';
import {NotificationService} from '@carm/_core/notification-mgt/facade/notifcation.service';
import {NotificationTypes} from '@carm/_core/notification-mgt/models/notification.model';
import {Store} from '@ngrx/store';
import {SnackbarComponent} from './shared/components/snackbar/snackbar.component';
import {environment} from '../environments/environment';
import {TranslateService} from '@ngx-translate/core';
import {takeUntil} from 'rxjs/operators';
import {BaseComponent} from './_utils/base.component';
import {EnvironmentName} from 'src/environments/baseEnvironmentConfigs';
import {Meta} from '@angular/platform-browser';
import {AuthService} from './_auth/spartacus/core/auth';
import {NavigationEnd, Router} from "@angular/router";
import {AnalyticsService} from '@carm/_core/analytics/analytics.service';
import { UpdateCoordinator } from '@carm/shared/services/update-coordinator.service';

@Component({
  selector: 'carm-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent extends BaseComponent implements OnInit, OnDestroy {

  constructor(
    protected store: Store<State>,
    protected metadataService: MetadataService,
    private notificationService: NotificationService,
    private snackBar: MatSnackBar,
    protected translateService: TranslateService,
    private authService: AuthService,
    private router: Router,
    @Inject(LOCALE_ID) protected localeId: string,
    private meta: Meta,
    private analyticsService: AnalyticsService,
    private updateCoordinator: UpdateCoordinator
  ) {
    super();
    this.analytics();
  }

  ngOnInit() {
    this.authService.isUserLoggedIn().subscribe(res => {
      if (res) {
        const currentLocal = (this.localeId === 'en' || this.localeId === 'fr') ? this.localeId : 'en';
        if (!this.metadataService.isMetadataLoaded) {
          this.metadataService.loadMetadata(currentLocal);
        }
      }
    })

    // Subscribe to the changes in notification store.
    this.notificationService
    .getNotification()
    .pipe(takeUntil(this.destroy$))
    .subscribe(notification => {
      if (!notification) {
        this.snackBar.dismiss(); // when notification store is empty hide the snackbar
        return;
      } else {
        this.openSnackBar(
          notification.type,
          notification.text,
          notification.title,
          notification.timeout
        ); // show snackbar
      }
    });


    // Subscribe to the changes in notification store.
    this.notificationService
    .getDeploymentNotification()
    .pipe(takeUntil(this.destroy$))
    .subscribe(notification => {
      if (!notification) {
        this.snackBar.dismiss(); // when notification store is empty hide the snackbar
        return;
      } else {
        this.openSnackBar(
          notification.type,
          notification.text,
          notification.title,
          notification.timeout,
          notification.disableClose,
          notification.horizontalPosition ?  notification.horizontalPosition as MatSnackBarHorizontalPosition: null,
          notification.verticalPosition ?  notification.verticalPosition as MatSnackBarVerticalPosition: null,
          notification.showTimer
        ); // show snackbar
      }
    });

    if (environment.name === EnvironmentName.Local) {
      localStorage.setItem('environment', EnvironmentName.Local);
    }

    this.appendMonerisScript();
  }

  private appendMonerisScript(): void {
    const monerisScriptFile = (environment.monerisTest === true) ? 'assets/moneris/chkt_v2.00_dev.js' : 'assets/moneris/chkt_v2.00_prod.js';
    const body = document.body as HTMLDivElement;
    const script = document.createElement('script');
    script.innerHTML = '';
    script.src = monerisScriptFile;
    script.async = true;
    script.defer = true;
    body.appendChild(script);
  }

  openSnackBar(
    messageTypeParam: NotificationTypes,
    messageParam: string,
    titleParam?: string,
    timeout?: number,
    disabledClose?: boolean,
    horizontalPosition?: MatSnackBarHorizontalPosition,
    verticalPosition?: MatSnackBarVerticalPosition,
    showTimer?: boolean
  ) {
    const matSnackBarConfig = new MatSnackBarConfig();
    matSnackBarConfig.duration = timeout;
    matSnackBarConfig.data = {
      message: messageParam,
      title: titleParam,
      messageType: messageTypeParam,
      snackbarRef: this.snackBar,
      disabledClose: disabledClose,
      showTimer,
    };

    if (disabledClose){
      matSnackBarConfig.duration = 0;
    }

    if (horizontalPosition){
      matSnackBarConfig.horizontalPosition = horizontalPosition;
    }

    if (verticalPosition){
      matSnackBarConfig.verticalPosition = verticalPosition;
    }

    matSnackBarConfig.panelClass = 'snackbar-custom-container'; // used in snackbar component template and scss
    this.snackBar.openFromComponent(SnackbarComponent, matSnackBarConfig);
  }

  analytics() {
    if (environment.analytics !== null && environment.analytics) {
      this.router.events.subscribe(event => {
        if (event && event instanceof NavigationEnd) {
          const eventData = this.analyticsService.analytics(event);
          this.analyticsService.sendAnalytics(eventData).pipe(takeUntil(this.destroy$)).subscribe();
        }
      });
    }
  }
}
